import { useState } from "react";
import { XIcon } from "lucide-react";

import { ExploreImagePanelLabel } from "@/components/tools/ExploreImagePanelLabel";

import { useErrorToast } from "@/hooks/useErrorToast";
import {
  EXPLORE_IMAGE_KEYWORD_MAX_LENGTH,
  EXPLORE_IMAGE_MAX_KEYWORDS,
  chamferClipPath,
} from "@/lib/exploreImages";

// Keyword chips plus a free-text input. Hands the whole new list to
// `onChange`, so the caller decides whether that saves or just updates a form.
export const ExploreImageKeywordInput = ({
  keywords,
  onChange,
}: {
  keywords: string[];
  onChange: (keywords: string[]) => void;
}) => {
  /* React Hooks */
  const [input, setInput] = useState("");

  const showErrorToast = useErrorToast();

  /* Handlers */
  // Adds whatever is typed, split on commas, with guac's normalization so the
  // chips don't jump after the save.
  const handleAdd = () => {
    const additions = input
      .split(",")
      .map((keyword) =>
        keyword.trim().toLowerCase().slice(0, EXPLORE_IMAGE_KEYWORD_MAX_LENGTH),
      )
      .filter((keyword) => keyword && !keywords.includes(keyword));
    setInput("");
    if (!additions.length) return;
    const next = [...new Set([...keywords, ...additions])];
    if (next.length > EXPLORE_IMAGE_MAX_KEYWORDS) {
      showErrorToast({
        description: `An image can have at most ${EXPLORE_IMAGE_MAX_KEYWORDS.toString()} keywords.`,
      });
      return;
    }
    onChange(next);
  };

  const handleRemove = (keyword: string) => {
    onChange(keywords.filter((k) => k !== keyword));
  };

  /* Render */
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <ExploreImagePanelLabel>Keywords</ExploreImagePanelLabel>
        <span className="font-plex-mono text-[10px] text-[#77787A]">
          {keywords.length}/{EXPLORE_IMAGE_MAX_KEYWORDS}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 bg-black/[0.03] p-2 ring-1 ring-inset ring-black/10 focus-within:ring-[#D17949] dark:bg-white/[0.04] dark:ring-white/10 dark:focus-within:ring-[#D17949]">
        {keywords.map((keyword) => (
          <span
            key={keyword}
            className="inline-flex items-center gap-1 bg-black/[0.06] py-0.5 pl-2 pr-1 font-plex-mono text-[11px] lowercase tracking-[0.04em] text-[#212327] dark:bg-white/[0.08] dark:text-[#EAEAEA]"
            style={{ clipPath: chamferClipPath(4) }}
          >
            {keyword}
            <button
              aria-label={`Remove ${keyword}`}
              className="p-0.5 text-[#77787A] hover:text-[#212327] dark:hover:text-white"
              onClick={() => {
                handleRemove(keyword);
              }}
              type="button"
            >
              <XIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          className="min-w-[120px] flex-1 bg-transparent py-0.5 text-sm text-[#212327] outline-none placeholder:text-[#9E9E9E] dark:text-white dark:placeholder:text-[#77787A]"
          onBlur={handleAdd}
          onChange={(e) => {
            setInput(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              handleAdd();
            } else if (e.key === "Backspace" && !input && keywords.length) {
              handleRemove(keywords[keywords.length - 1]);
            }
          }}
          placeholder={keywords.length ? "Add…" : "tig welding, pipe…"}
          value={input}
        />
      </div>
      <span className="text-[11px] text-[#77787A]">
        Searchable. Enter or a comma adds one.
      </span>
    </div>
  );
};
